import { useRef, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  TextB,
  TextItalic,
  TextUnderline,
  TextHTwo,
  ListBullets,
  ListNumbers,
  LinkSimple,
  Eraser,
} from "@phosphor-icons/react";

const SANITIZE = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "b", "em", "i", "u", "h1", "h2", "h3",
    "ul", "ol", "li", "a", "blockquote", "span", "div",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "style"],
};

const MERGE_FIELDS = [
  ["{company_name}", "Company name"],
  ["{customer_name}", "Customer name"],
  ["{contact_name}", "Contact name"],
  ["{project_name}", "Project name"],
  ["{job_address}", "Job address"],
  ["{material}", "Material"],
  ["{quote_code}", "Quote code"],
  ["{total}", "Total"],
  ["{sqft}", "Sq ft"],
  ["{date}", "Date"],
];

const ToolBtn = ({ onClick, title, testid, children }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white"
  >
    {children}
  </button>
);

export const RichTextEditor = ({ value, onChange, dataTestId, mergeFields = MERGE_FIELDS }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el && (value || "") !== el.innerHTML) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = useCallback(() => {
    if (!ref.current) return;
    onChange(DOMPurify.sanitize(ref.current.innerHTML, SANITIZE));
  }, [onChange]);

  const cmd = (command, arg) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertField = (token) => {
    if (!token) return;
    ref.current?.focus();
    document.execCommand("insertText", false, token);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (url) cmd("createLink", url);
  };

  return (
    <div
      className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950"
      data-testid={dataTestId}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-zinc-800 bg-zinc-900/60 p-1.5">
        <ToolBtn onClick={() => cmd("bold")} title="Bold" testid={`${dataTestId}-bold`}>
          <TextB size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={() => cmd("italic")} title="Italic" testid={`${dataTestId}-italic`}>
          <TextItalic size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={() => cmd("underline")} title="Underline" testid={`${dataTestId}-underline`}>
          <TextUnderline size={16} weight="bold" />
        </ToolBtn>
        <span className="mx-1 h-5 w-px bg-zinc-800" />
        <ToolBtn onClick={() => cmd("formatBlock", "<h2>")} title="Heading" testid={`${dataTestId}-h2`}>
          <TextHTwo size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={() => cmd("insertUnorderedList")} title="Bulleted list" testid={`${dataTestId}-ul`}>
          <ListBullets size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={() => cmd("insertOrderedList")} title="Numbered list" testid={`${dataTestId}-ol`}>
          <ListNumbers size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={addLink} title="Add link" testid={`${dataTestId}-link`}>
          <LinkSimple size={16} weight="bold" />
        </ToolBtn>
        <ToolBtn onClick={() => cmd("removeFormat")} title="Clear formatting" testid={`${dataTestId}-clear`}>
          <Eraser size={16} weight="bold" />
        </ToolBtn>
        <select
          data-testid={`${dataTestId}-insert-field`}
          onChange={(e) => {
            insertField(e.target.value);
            e.target.value = "";
          }}
          defaultValue=""
          className={`ml-auto rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-xs text-zinc-300 outline-none focus:border-indigo-400/50 ${
            mergeFields.length ? "" : "hidden"
          }`}
        >
          <option value="">Insert field…</option>
          {mergeFields.map(([token, label]) => (
            <option key={token} value={token}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-testid={`${dataTestId}-area`}
        className="rte-prose min-h-[220px] max-h-[420px] overflow-auto p-4 text-sm leading-relaxed text-zinc-200 outline-none"
      />
    </div>
  );
};
